import { AttributeTypeENUM, FlippingAxisEnum } from '../../enums/ProductEnums';
import { AttributeDTO } from '../DTOs/AttributeDTO';
import {
  AttributeValueDTO,
  FlipValueDTO,
  MaterialValueDTO,
  ModelValueDTO,
  VariationValueDTO,
} from '../DTOs/AttributeValueDTO';
import { LocalizedTextDTO } from '../DTOs/LocalizedTextDTO';
import { Attribute } from './Attribute';
import { LocalizedText } from './LocalizedText';
import { IdMapper, ProductBlueprint } from './ProductBlueprint';
import { ProductRelationship } from './ProductRelationship';
import { TextureData } from './TextureData';

export class AttributeValue {
  public static fromDTO(
    attributeValueDTO: AttributeValueDTO,
    type: AttributeTypeENUM,
    availibleBlueprints?: ProductBlueprint[],
  ): AttributeValue | MaterialValue | VariationValue | ModelValue | FlipValue {
    switch (type) {
      case AttributeTypeENUM.MaterialChange: {
        return MaterialValue.fromMaterialValueDTO(attributeValueDTO as MaterialValueDTO);
      }

      case AttributeTypeENUM.VariationChange: {
        return VariationValue.fromVariationValueDTO(attributeValueDTO as VariationValueDTO, availibleBlueprints);
      }

      case AttributeTypeENUM.ModelChange: {
        return ModelValue.fromModelValueDTO(attributeValueDTO as ModelValueDTO, availibleBlueprints);
      }

      case AttributeTypeENUM.BaseAttribute:
      case AttributeTypeENUM.NumericChange: {
        const attributes: Attribute[] = [];

        for (const attributeDTO of attributeValueDTO.attributes) {
          attributes.push(Attribute.fromDTO(attributeDTO));
        }

        return new AttributeValue(
          attributeValueDTO.id.toString(),
          attributeValueDTO.thumbnailUrl,
          attributeValueDTO.infoText?.map((text) => LocalizedText.fromDTO(text)),
          attributes,
          attributeValueDTO.externalId,
          attributeValueDTO.attributeValueIds ? attributeValueDTO.attributeValueIds.map((id) => id.toString()) : [],
          undefined,
          attributeValueDTO.builderData,
          attributeValueDTO.displayParameters,
        );
      }

      case AttributeTypeENUM.FlipChange: {
        return FlipValue.fromFlipValueDTO(attributeValueDTO as FlipValueDTO);
      }

      default: {
        const attributes: Attribute[] = [];

        for (const attributeDTO of attributeValueDTO.attributes) {
          attributes.push(Attribute.fromDTO(attributeDTO));
        }

        return new AttributeValue(
          attributeValueDTO.id.toString(),
          attributeValueDTO.thumbnailUrl,
          attributeValueDTO.infoText?.map((text) => LocalizedText.fromDTO(text)),
          attributes,
          attributeValueDTO.externalId,
          attributeValueDTO.attributeValueIds ? attributeValueDTO.attributeValueIds.map((id) => id.toString()) : [],
          undefined,
          attributeValueDTO.builderData,
          attributeValueDTO.displayParameters,
        );
      }
    }
  }

  id: string;
  thumbnailUrl: string;
  infoText: LocalizedText[];
  attributes: Attribute[];
  externalId: string;
  referenceValues: AttributeValue[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builderData?: any;

  attributeValues: AttributeValue[] = [];
  attributeValuesMap: Map<string, AttributeValue> = new Map<string, AttributeValue>();
  attributeValueIds: string[];
  attributeValueIdsSet: Set<string>;

  displayParameters: any = null;

  constructor(
    id: string,
    thumbnailUrl: string,
    infoText: LocalizedText[],
    attributes: Attribute[],
    externalId: string,
    attributeValueIds: string[],
    referenceValues: AttributeValue[] = [],
    builderData?: string,
    displayParameters?: string,
  ) {
    this.id = id;
    this.thumbnailUrl = thumbnailUrl;
    this.infoText = infoText;
    this.attributes = attributes;
    this.externalId = externalId;
    this.attributeValueIds = attributeValueIds;
    this.attributeValueIdsSet = new Set(this.attributeValueIds);
    this.referenceValues = referenceValues;
    if (builderData) {
      this.builderData = JSON.parse(builderData);
    }
    if (displayParameters) {
      this.displayParameters = JSON.parse(displayParameters);
    }
  }

  public toDTO() {
    const attributeValueDTOs: AttributeValueDTO[] = [];
    for (const attributeValue of this.attributeValues) {
      attributeValueDTOs.push(attributeValue.toDTO());
    }

    const attributeDTOs: AttributeDTO[] = [];
    for (const attribute of this.attributes) {
      attributeDTOs.push(attribute.toDTO());
    }

    const infoTextDTOs: LocalizedTextDTO[] = [];
    for (const infoText of this.infoText) {
      infoTextDTOs.push(infoText.toDTO());
    }

    return new AttributeValueDTO(
      Number(this.id),
      this.thumbnailUrl,
      infoTextDTOs,
      attributeDTOs,
      this.externalId,
      this.attributeValueIds.map((attributeValueId) => Number(attributeValueId)),
      this.builderData,
    );
  }

  public addReferenceValue(referenceValue: AttributeValue) {
    this.referenceValues.push(referenceValue);
  }

  public populateProductBlueprintReferences(availibleBlueprints: ProductBlueprint[], idMapper: IdMapper) {
    for (const attribute of this.attributes) {
      attribute.populateProductBlueprintReferences(availibleBlueprints, idMapper);
    }
  }

  //skor isti algoritem ko pri populiranju atributov
  private _recursivelyPopulateWithAttributeValues(
    attributes: Attribute[],
    idMapper: IdMapper,
    productBlueprint: ProductBlueprint,
  ) {
    for (const attribute of attributes) {
      //vsakič preverimo če smo konec
      if (this.attributeValueIds.length === this.attributeValues.length) {
        break;
      }
      //sproti zabeležujemo, verjetno prevečkrat
      idMapper.attributeMap.set(attribute.id, { attribute: attribute, productBlueprint: productBlueprint });
      for (const attributeValue of attribute.attributeValues) {
        idMapper.attributeValueMap.set(attributeValue.id, attributeValue);
        if (this.attributeValueIdsSet.has(attributeValue.id)) {
          attributeValue.addReferenceValue(this);

          this.attributeValues.push(attributeValue);
          this.attributeValuesMap.set(attributeValue.id, attributeValue);
        }

        this._recursivelyPopulateWithAttributeValues(attributeValue.attributes, idMapper, productBlueprint);
      }
    }
  }

  public populateWithAttributeValues(
    availibleBlueprints: ProductBlueprint[],
    ownerBlueprint: ProductBlueprint,
    idMapper: IdMapper,
  ) {
    //preverimo če lahko kr iz mapa vse narejimo
    for (const referencedAttributeValueId of this.attributeValueIds) {
      if (this.attributeValuesMap.get(referencedAttributeValueId)) continue;

      const mappedAttributeValue = idMapper.attributeValueMap.get(referencedAttributeValueId);
      if (!mappedAttributeValue) continue;

      this.attributeValues.push(mappedAttributeValue);
      this.attributeValuesMap.set(mappedAttributeValue.id, mappedAttributeValue);

      mappedAttributeValue.addReferenceValue(this);
    }

    for (const productBlueprint of availibleBlueprints) {
      if (this.attributeValueIds.length === this.attributeValues.length) {
        break;
      }
      this._recursivelyPopulateWithAttributeValues(productBlueprint.attributes, idMapper, productBlueprint);
    }

    for (const attribute of this.attributes) {
      attribute.populateWithAttributes(availibleBlueprints, ownerBlueprint, idMapper);
    }
  }

  public clone() {
    return new AttributeValue(
      this.id,
      this.thumbnailUrl,
      this.infoText,
      this.attributes,
      this.externalId,
      this.attributeValueIds,
      this.referenceValues,
      this.builderData,
      this.displayParameters,
    );
  }
}

export class VariationValue extends AttributeValue {
  public static fromVariationValueDTO(attributeValueDTO: VariationValueDTO, availibleBlueprints?: ProductBlueprint[]) {
    const attributes: Attribute[] = [];
    for (const attribute of attributeValueDTO.attributes) {
      attributes.push(Attribute.fromDTO(attribute));
    }

    const foundProductBlueprint = availibleBlueprints?.find(
      (blueprint) => blueprint.id === attributeValueDTO.productBlueprintId.toString(),
    );

    return new VariationValue(
      attributeValueDTO.id.toString(),
      attributeValueDTO.thumbnailUrl,
      attributeValueDTO.infoText?.map((text) => LocalizedText.fromDTO(text)),
      attributes,
      attributeValueDTO.externalId,
      attributeValueDTO.attributeValueIds ? attributeValueDTO.attributeValueIds.map((id) => id.toString()) : [],
      undefined,
      foundProductBlueprint ? foundProductBlueprint : attributeValueDTO.productBlueprintId.toString(),
      attributeValueDTO.builderData,
      attributeValueDTO.displayParameters,
    );
  }

  productBlueprint: ProductBlueprint | string;
  constructor(
    id: string,
    thumbnailUrl: string,
    infoText: LocalizedText[],
    attributes: Attribute[],
    externalId: string,
    attributeValueIds: string[],
    referenceValues: AttributeValue[] = [],
    productBlueprint: ProductBlueprint | string,
    builderData?: string,
    displayParameters?: string,
  ) {
    super(
      id,
      thumbnailUrl,
      infoText,
      attributes,
      externalId,
      attributeValueIds,
      referenceValues,
      builderData,
      displayParameters,
    );
    this.productBlueprint = productBlueprint;
  }

  public override populateProductBlueprintReferences(availibleBlueprints: ProductBlueprint[], idMapper: IdMapper) {
    super.populateProductBlueprintReferences(availibleBlueprints, idMapper);
    const foundProductBlueprint = availibleBlueprints.find((productBlueprint) => {
      productBlueprint.id === this.productBlueprint;
    });
    if (!foundProductBlueprint) {
      throw new Error('Could not populate product relationship, because blueprint was not in availible blueprints');
    }

    this.productBlueprint = foundProductBlueprint;
  }

  public override clone() {
    return new VariationValue(
      this.id,
      this.thumbnailUrl,
      this.infoText,
      this.attributes,
      this.externalId,
      this.attributeValueIds,
      this.referenceValues,
      this.productBlueprint,
      this.builderData,
      this.displayParameters,
    );
  }
}
export class MaterialValue extends AttributeValue {
  public static fromMaterialValueDTO(attributeValueDTO: MaterialValueDTO) {
    const attributes: Attribute[] = [];

    for (const attribute of attributeValueDTO.attributes) {
      attributes.push(Attribute.fromDTO(attribute));
    }

    return new MaterialValue(
      attributeValueDTO.id.toString(),
      attributeValueDTO.thumbnailUrl,
      attributeValueDTO.infoText?.map((text) => LocalizedText.fromDTO(text)),
      attributes,
      attributeValueDTO.externalId,
      attributeValueDTO.attributeValueIds ? attributeValueDTO.attributeValueIds.map((id) => id.toString()) : [],
      undefined,
      TextureData.fromDTO(attributeValueDTO.textureData),
      attributeValueDTO.materialInfo,
      attributeValueDTO.builderData,
      attributeValueDTO.displayParameters,
    );
  }

  textureData: TextureData;
  materialInfo?: string;

  constructor(
    id: string,
    thumbnailUrl: string,
    infoText: LocalizedText[],
    attributes: Attribute[],
    externalId: string,
    attributeValueIds: string[],
    referenceValues: AttributeValue[] = [],
    textureData: TextureData,
    materialInfo?: string,
    builderData?: string,
    displayParameters?: string,
  ) {
    super(
      id,
      thumbnailUrl,
      infoText,
      attributes,
      externalId,
      attributeValueIds,
      referenceValues,
      builderData,
      displayParameters,
    );
    this.textureData = textureData;
    this.materialInfo = materialInfo;
  }

  public override toDTO() {
    const attributeValueDTOs: AttributeValueDTO[] = [];
    for (const attributeValue of this.attributeValues) {
      attributeValueDTOs.push(attributeValue.toDTO());
    }

    const attributeDTOs: AttributeDTO[] = [];
    for (const attribute of this.attributes) {
      attributeDTOs.push(attribute.toDTO());
    }

    const infoTextDTOs: LocalizedTextDTO[] = [];
    for (const infoText of this.infoText) {
      infoTextDTOs.push(infoText.toDTO());
    }

    return new MaterialValueDTO(
      Number(this.id),
      this.thumbnailUrl,
      infoTextDTOs,
      attributeDTOs,
      this.externalId,
      this.attributeValueIds.map((attributeValueId) => Number(attributeValueId)),
      this.textureData.toDTO(),
      this.materialInfo,
      this.builderData,
      this.displayParameters,
    );
  }

  public override clone() {
    return new MaterialValue(
      this.id,
      this.thumbnailUrl,
      this.infoText,
      this.attributes,
      this.externalId,
      this.attributeValueIds,
      this.referenceValues,
      this.textureData,
      this.materialInfo,
      this.builderData,
      this.displayParameters,
    );
  }
}
export class ModelValue extends AttributeValue {
  public static fromModelValueDTO(attributeValueDTO: ModelValueDTO, availibleBlueprints?: ProductBlueprint[]) {
    const attributes: Attribute[] = [];

    for (const attribute of attributeValueDTO.attributes) {
      attributes.push(Attribute.fromDTO(attribute));
    }

    const productRelationships: ProductRelationship[] = [];

    if (attributeValueDTO.productRelationships) {
      for (const productRelationshipDTO of attributeValueDTO.productRelationships) {
        productRelationships.push(ProductRelationship.fromDTO(productRelationshipDTO, availibleBlueprints));
      }
    }

    return new ModelValue(
      attributeValueDTO.id.toString(),
      attributeValueDTO.thumbnailUrl,
      attributeValueDTO.infoText?.map((text) => LocalizedText.fromDTO(text)),
      attributes,
      attributeValueDTO.externalId,
      attributeValueDTO.attributeValueIds ? attributeValueDTO.attributeValueIds.map((id) => id.toString()) : [],
      undefined,
      productRelationships,
      attributeValueDTO.builderData,
      attributeValueDTO.displayParameters,
    );
  }

  productRelationships: ProductRelationship[];

  constructor(
    id: string,
    thumbnailUrl: string,
    infoText: LocalizedText[],
    attributes: Attribute[],
    externalId: string,
    attributeValueIds: string[],
    referenceValues: AttributeValue[] = [],
    productRelationshipGroup: ProductRelationship[],
    builderData?: string,
    displayParameters?: string,
  ) {
    super(
      id,
      thumbnailUrl,
      infoText,
      attributes,
      externalId,
      attributeValueIds,
      referenceValues,
      builderData,
      displayParameters,
    );
    this.productRelationships = productRelationshipGroup;
  }

  public override() {
    return new ModelValue(
      this.id,
      this.thumbnailUrl,
      this.infoText,
      this.attributes,
      this.externalId,
      this.attributeValueIds,
      this.referenceValues,
      this.productRelationships,
      this.builderData,
      this.displayParameters,
    );
  }

  public override populateProductBlueprintReferences(availibleBlueprints: ProductBlueprint[], idMapper: IdMapper) {
    for (const productRelationship of this.productRelationships) {
      productRelationship.populateProductBlueprintReferences(availibleBlueprints, idMapper);
    }
    super.populateProductBlueprintReferences(availibleBlueprints, idMapper);
  }
}
export class FlipValue extends AttributeValue {
  public static fromFlipValueDTO(attributeValueDTO: FlipValueDTO) {
    const attributes: Attribute[] = [];

    for (const attribute of attributeValueDTO.attributes) {
      attributes.push(Attribute.fromDTO(attribute));
    }

    return new FlipValue(
      attributeValueDTO.id.toString(),
      attributeValueDTO.thumbnailUrl,
      attributeValueDTO.infoText?.map((text) => LocalizedText.fromDTO(text)),
      attributes,
      attributeValueDTO.externalId,
      attributeValueDTO.attributeValueIds ? attributeValueDTO.attributeValueIds.map((id) => id.toString()) : [],
      undefined,
      attributeValueDTO.flippingAxis,
      attributeValueDTO.flippingValue,
      attributeValueDTO.builderData,
      attributeValueDTO.displayParameters,
    );
  }

  flippingAxis: FlippingAxisEnum;
  flippingValue: number;

  constructor(
    id: string,
    thumbnailUrl: string,
    infoText: LocalizedText[],
    attributes: Attribute[],
    externalId: string,
    attributeValueIds: string[],
    referenceValues: AttributeValue[] = [],
    flippingAxis: FlippingAxisEnum,
    flippingValue: number,
    builderData?: string,
    displayParameters?: string,
  ) {
    super(
      id,
      thumbnailUrl,
      infoText,
      attributes,
      externalId,
      attributeValueIds,
      referenceValues,
      builderData,
      displayParameters,
    );

    this.flippingAxis = flippingAxis;
    this.flippingValue = flippingValue;
  }

  public override clone() {
    return new FlipValue(
      this.id,
      this.thumbnailUrl,
      this.infoText,
      this.attributes,
      this.externalId,
      this.attributeValueIds,
      this.referenceValues,
      this.flippingAxis,
      this.flippingValue,
      this.builderData,
      this.displayParameters,
    );
  }
}
